import { useMemo, useState } from 'react';
import { Box, Button, Tooltip } from '@mui/material';

import { DataTable, type TableColumn, HandscoreIcon } from '@/src/ui';
import type { QuestionDTO, StudentExamAttemptDTO } from '../models/handscoreDTOs';
import ByQuestionModal from './ByQuestionModal';
import ByStudentModal from './ByStudentModal';
import { ReportHeaderStyle, ReportCellStyle, utcToLocal, formatOptions } from '@/src/utils';

interface HandScoreQuestionsTableProps {
    questions: QuestionDTO[];
    studentExamAttempts: StudentExamAttemptDTO[];
    updateQuestionCorrectStatus: (
        attemptQuestionId: number,
        studentUserId: number,
        isCorrect: boolean
    ) => void;
    searchString: string;
    activeTab: 'questions' | 'students';
}

export default function HandScoreQuestionsTable({
    questions,
    studentExamAttempts,
    updateQuestionCorrectStatus,
    searchString,
    activeTab,
}: HandScoreQuestionsTableProps) {
    const [openQuestionModal, setOpenQuestionModal] = useState<number | null>(null);
    const [openStudentModal, setOpenStudentModal] = useState<number | null>(null);

    // Search filters
    const filteredQuestions = useMemo(() => {
        const search = searchString.trim().toLowerCase();
        if (!search) return questions;
        return questions.filter(
            (q) =>
                `${q.questionId}`.toLowerCase().includes(search) ||
                q.students.some((s) => s.studentName.toLowerCase().includes(search))
        );
    }, [questions, searchString]);

    const filteredStudentExamAttempts = useMemo(() => {
        const search = searchString.trim().toLowerCase();
        if (!search) return studentExamAttempts;
        return studentExamAttempts.filter(
            (s) =>
                s.studentName.toLowerCase().includes(search) ||
                s.examName.toLowerCase().includes(search)
        );
    }, [studentExamAttempts, searchString]);

    const questionColumns = useMemo<TableColumn[]>(() => {
        return [
            {
                dataKey: 'questionTitle',
                header: 'Question',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'questionType',
                header: 'Question Type',
                align: 'left',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'status',
                header: 'Status',
                align: 'left',
                allowSort: false,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row) => {
                    const incomplete = row.students.some((s: any) => !s.isScored);
                    return (
                        <Box
                            component="span"
                            sx={{
                                display: 'inline-block',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                                fontWeight: 500,
                                color: 'white',
                                bgcolor: incomplete ? 'warning.main' : 'var(--pm-secondary)',
                            }}
                        >
                            {incomplete ? 'Incomplete' : 'Complete'}
                        </Box>
                    );
                },
            },
            {
                dataKey: 'actions',
                header: 'Actions',
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row) => (
                    <Tooltip title="View" arrow>
                        <Button
                            size="small"
                            className="icon-button"
                            startIcon={<HandscoreIcon style={{ width: '28px', height: '28px' }} />}
                            onClick={() => setOpenQuestionModal(row.questionId)}
                            sx={{
                                '&:hover': {
                                    backgroundColor: 'transparent',
                                },
                            }}
                        />
                    </Tooltip>
                ),
            },
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredQuestions]);

    const studentExamAttemptColumns = useMemo<TableColumn[]>(() => {
        return [
            {
                dataKey: 'studentName',
                header: 'Student Name',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'examName',
                header: 'Assessment',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'submittedAt',
                header: 'Submitted At',
                align: 'left',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row) => {
                    if (!row.submittedAt) return '-';
                    return utcToLocal(row.submittedAt).toLocaleString(undefined, formatOptions);
                },
            },
            {
                dataKey: 'status',
                header: 'Status',
                align: 'left',
                allowSort: false,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row) => {
                    const incomplete = row.questions.some((q: any) => !q.isScored);
                    return (
                        <Box
                            component="span"
                            sx={{
                                display: 'inline-block',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                                fontWeight: 500,
                                color: 'white',
                                bgcolor: incomplete ? 'warning.main' : 'primary.main',
                            }}
                        >
                            {incomplete ? 'Incomplete' : 'Complete'}
                        </Box>
                    );
                },
            },
            {
                dataKey: 'actions',
                header: 'Actions',
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row) => (
                    <Tooltip title="View" arrow>
                        <Button
                            size="small"
                            startIcon={<HandscoreIcon />}
                            onClick={() => setOpenStudentModal(row.attemptId)}
                            sx={{
                                '&:hover': {
                                    backgroundColor: 'transparent',
                                },
                            }}
                        />
                    </Tooltip>
                ),
            },
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredStudentExamAttempts]);

    return (
        <Box>
            {activeTab === 'questions' ? (
                <DataTable
                    data={filteredQuestions}
                    columns={questionColumns}
                    tableName="Questions"
                    defaultSortColumn="questionId"
                    defaultSortDirection="asc"
                    alternatingRowColor
                />
            ) : (
                <DataTable
                    data={filteredStudentExamAttempts}
                    columns={studentExamAttemptColumns}
                    tableName="Students"
                    defaultSortColumn="attemptId"
                    defaultSortDirection="asc"
                    alternatingRowColor
                />
            )}

            {/* Modals */}
            {openQuestionModal && (
                <ByQuestionModal
                    open={!!openQuestionModal}
                    onClose={() => setOpenQuestionModal(null)}
                    updateQuestionCorrectStatus={updateQuestionCorrectStatus}
                    questionId={openQuestionModal}
                    questions={questions}
                />
            )}
            {openStudentModal && (
                <ByStudentModal
                    open={!!openStudentModal}
                    onClose={() => setOpenStudentModal(null)}
                    updateQuestionCorrectStatus={updateQuestionCorrectStatus}
                    studentExamAttemptId={openStudentModal}
                    studentExamAttempts={studentExamAttempts}
                />
            )}
        </Box>
    );
}
